/**
 * Smart cuts for the patient band. Each function tries candidates from the
 * fullest to the shortest and returns the first one `fits` accepts. `fits`
 * measures the real rendered width, so these stay pure.
 */

/**
 * "Maria Lopez-Gutierrez" -> "Maria Lopez…" -> "Maria Lop…" -> "Maria…".
 * The last candidate is cut further by the element's CSS `truncate` when needed.
 */
export function fitName(
  first: string,
  last: string,
  fits: (text: string) => boolean
): string {
  const full = `${first} ${last}`;
  if (fits(full)) return full;

  const part = last.search(/[-\s]/);
  if (part > 0) {
    const byPart = `${first} ${last.slice(0, part)}…`;
    if (fits(byPart)) return byPart;
  }

  for (let n = last.length - 1; n >= 1; n--) {
    const cut = `${first} ${last.slice(0, n)}…`;
    if (fits(cut)) return cut;
  }
  return `${first}…`;
}

/**
 * Keeps the head and the tail of a long value, where a record number differs
 * most: "MRN KINNSER-2026…AL-HH-0042-A". A value that fits is never cut.
 */
export function fitMiddle(
  prefix: string,
  value: string,
  fits: (text: string) => boolean
): string {
  const full = `${prefix}${value}`;
  if (fits(full)) return full;

  const cutAt = (n: number) =>
    `${prefix}${value.slice(0, n)}…${value.slice(-n)}`;
  for (let n = Math.floor((value.length - 1) / 2); n >= 1; n--) {
    const cut = cutAt(n);
    if (fits(cut)) return cut;
  }
  return cutAt(1);
}
